import {ManagedScene2} from "../ManagedScene2";
import {DrawFrmApp} from "../../drawfrm/DrawFrmApp";
import {FrmSceneLayout} from "../../drawfrm/FrmSceneLayout";
import {ElevationApp} from "../../ElevationApp";
import {AddFx, ChildView} from "../../shared-2d/ChildView";
import {FrameBlockRow} from "./FrameBlockRow";
import {FrameBlockColumn} from "./FrameBlockColumn";
import {MdFrameBlock, sticksById} from "../../model/frame/MdFrameBlock";
import {recalcLayout} from "./Layout";
import {FrameBlockExt, TFrameBlock} from "./FrameBlockExt";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {FrameBlockItem} from "./FrameBlockItem";
import {detectCorners, FrameCorner, resolveTerminations} from "./Terminations";
import {meetAcrossSticks, mergeJoinedSteps} from "./JoinedSteps";
import {normalizeAirGaps, normalizeAngles, normalizeSlopes, normalizeEdgeSticks, normalizeSteps, setStickAngle, simplifyTree} from "./BlockOps";
import {detectJoints} from "./Joints";
import {StickMaterial, stockSticks} from "./StickMaterial";
import {MdFrameJoint} from "../../model/frame/MdFrameBlock";
import {frameBlocksDebugTree} from "./debugFrameBlocks";
import {FrameOuterDimension} from "./FrameOuterDimension";
import {ExportFrameDimensions} from "./ExportFrameDimensions";
import {DoorNominalDimensions} from "./DoorNominalDimensions";
import {CrosshairRect, HardwareCrosshairThing} from "../hardware/HardwareCrosshairThing";
import {MdHardwarePlacement} from "../../model/hardware/MdHardware";
import {mountsOnFace, ruleForItem} from "../../model/hardware/MdHardwareRules";
import {doorFaceFromLocationOne, hingeSideOrDefault} from "../frameProfile/Helpers";
import {HardwareItem} from "../../../../../../api/Hardware";
import {GlyphLeaf} from "../../shared-2d/hardwareGlyphs";
import {StickAngleDimension} from "./StickAngleDimension";
import {AnchorMarks} from "./AnchorMarks";
import {normalizeAnchors} from "./Anchors";

export class FrameGridView extends ChildView {
    grid: TFrameBlock;
    parentThing: DrawFrmThing;

    // overall outside-to-outside frame dimensions (horizontal + vertical), redrawn
    // each render. Owned by the view (not a block) so it survives grid rebuilds.
    private outerDimension: FrameOuterDimension;

    // engineering-drawing dimensioning stacked outside the frame, drawn only while
    // the externalDimensions view flag is on (the screen export). When on, the
    // inline outer/per-cell dimensions hide themselves.
    private exportDimensions: ExportFrameDimensions;
    private doorDimensions: DoorNominalDimensions;

    // frame-mounted hardware crosshairs (items with a frame prep). Owned by the
    // view like outerDimension so they survive grid rebuilds; rebuilt when the
    // hardware list changes, re-attached + redrawn each render.
    private hardwareCrosshairs: HardwareCrosshairThing[] = [];

    // every StickMaterial in the grid (outline + interior), registered at
    // creation. Joint detection (cross/tee) reads their laid-out positions; cleared
    // and repopulated on a full rebuild.
    sticks: StickMaterial[] = [];

    // the outer corners of the frame outline from the last render (see Terminations.ts).
    corners: FrameCorner[] = [];

    // the plan-angle label of every angled mullion, by stick id (see
    // StickAngleDimension). Owned by the view like outerDimension so they survive grid
    // rebuilds; re-attached and redrawn each render.
    private angleDimensions = new Map<string, StickAngleDimension>();

    // the frame anchors on the outline sticks. Owned by the view like outerDimension so they
    // survive grid rebuilds; re-attached and redrawn each render.
    anchorMarks: AnchorMarks;

    constructor(elev: ElevationApp, add: AddFx) {
        super(elev, add);

        elev.frameGrid = this;

        this.parentThing = new DrawFrmThing();
        this.add(this.parentThing);
        this.grid = this.createFrameBlock(this, elev.frameSpec.frameBlocks);
        this.outerDimension = new FrameOuterDimension(this);
        this.exportDimensions = new ExportFrameDimensions(this);
        this.doorDimensions = new DoorNominalDimensions(this);
        this.anchorMarks = new AnchorMarks(elev, () => this.sticks);
        this.buildHardwareItems();

        // redraw when the external-dimensions flag flips (the export toggles it).
        this.trackSubscription(elev.viewSettings.externalDimensions.subscribe(() => {
            this.render();
        }));

        // rebuild the frame crosshairs when the placed-hardware list changes.
        this.trackSubscription(elev.hardware.listChanged.subscribe(() => {
            this.buildHardwareItems();
            this.render();
        }));

        // when the block model changes (e.g. a cell is converted lite <->
        // fixedPanel), reconcile the existing grid against it, then redraw.
        this.trackSubscription(elev.frameSpec.frameBlocksChanged.subscribe(() => {
            this.reconcileBlocks();
            this.render();
        }));

        // the frame-wide joint default feeds termination resolution (corners with
        // no per-corner override follow it), so re-render when it changes. The
        // subscribe callback receives the new value directly, so pass it through.
        this.trackSubscription(elev.frameSpec.frameBlockJointDefault.subscribe(value => {
            this.render(value);
        }));

        // the frame undercut shortens the door cell (see MdFrameSpec's door height),
        // so re-solve the grid when it changes. A plain render (no reconcile — the
        // block model shape is unchanged, only sizes).
        this.trackSubscription(elev.frameSpec.undercutFrame.subscribe(() => {
            this.render();
        }));

        this.render();

        window["zzDebugFrameBlocks"] = () => {
            debugFrameBlocks(elev.frameSpec.frameBlocks);
        }
    }

    // bring the runtime grid back in sync with frameSpec.frameBlocks. Leaf items
    // are matched by id: a kind change (lite <-> fixedPanel) just rebuilds that
    // item's face; any structural change (an item added/removed/reordered) is
    // handled by rebuilding the whole grid.
    reconcileBlocks() {
        const modelOrder = collectModelItemIds(this.elev.frameSpec.frameBlocks);
        const runtimeItems = this.grid.getItems() as FrameBlockItem[];
        const runtimeOrder = runtimeItems.map(i => i.model.id);

        if(!sameOrder(modelOrder, runtimeOrder)) {
            this.rebuildGrid();
            return;
        }

        for(const item of runtimeItems) {
            item.reconcileInner();
        }
    }

    private rebuildGrid() {
        this.parentThing.reset();
        this.sticks = [];

        this.grid = this.createFrameBlock(this, this.elev.frameSpec.frameBlocks);
    }

    // called by blocks as they create their StickMaterials (outline + interior).
    registerStick(stick: StickMaterial) {
        this.sticks.push(stick);
    }

    // (re)build the frame crosshair Things from the placed-hardware list (frame
    // placements only). Destroy old ones first so removed items leave no orphan.
    private buildHardwareItems() {
        for(const t of this.hardwareCrosshairs) {
            if(!t.destroyed) t.destroy();
        }
        this.hardwareCrosshairs = [];

        // the frame-only views draw no hardware crosshairs.
        if(this.elev.viewSettings.frameOnly()) return;

        const hw = this.elev.hardwareGroup?.hardware ?? [];
        // the grid is seen from location one: frame hardware that mounts on the face of
        // the leaf away from the viewer (an operator on the pull side) stays hidden, as
        // the leaf faces do with their door placements.
        const face = doorFaceFromLocationOne(this.elev, true);
        const framePlacements = this.elev.hardware.list.filter(p => p.target === "frame");
        for(const placement of framePlacements) {
            const item = hw.find(h => h.product === placement.productId);
            if(!item) continue; // product not in the group anymore — skip
            if(!mountsOnFace(item, face)) continue;

            const thing = new HardwareCrosshairThing({
                elev: this.elev,
                item: placement,
                label: item.productCode.slice(0, 6),
                product: item,
                // frame crosshairs measure to the 4 sides of the jamb/head/sill
                // stick they're mounted on, and re-home onto whichever outline
                // stick they're dragged over.
                target: {
                    rect: () => this.frameStickRect(placement, item),
                    rebindTo: (center) => {
                        const id = this.frameStickIdAt(center);
                        if(id) placement.stickId = id;
                    },
                    leaves: () => this.doorLeaves(),
                },
                onSelect: (additive) => this.selectHardwareItem(placement, additive),
            });
            this.hardwareCrosshairs.push(thing);
        }
    }

    private selectHardwareItem(item: MdHardwarePlacement, additive: boolean) {
        this.elev.viewSettings.selectItem({type: "hardware-item", id: item.id, kind: item.target, productId: item.productId}, additive);
        this.elev.viewSettings.activeTab.emit("selected");
    }

    // the outline jamb/head/sill sticks (the mountable frame materials). Read from
    // the drawn outline so it reflects the current grid.
    private outlineStickMats(): StickMaterial[] {
        const mats = new Map<string, StickMaterial>();
        for(const p of this.grid.outlineMats()) {
            if(!mats.has(p.mat.id)) mats.set(p.mat.id, p.mat);
        }
        return Array.from(mats.values());
    }

    private stickHasArea(s: StickMaterial): boolean {
        return s.width.get() > 0 && s.height.get() > 0;
    }

    private stickRect(s: StickMaterial): CrosshairRect {
        const x0 = s.positionX, y0 = s.positionY;
        return {x0, y0, x1: x0 + s.width.get(), y1: y0 + s.height.get()};
    }

    // the door leaves in the grid, for the frame glyphs that draw onto them.
    private doorLeaves(): GlyphLeaf[] {
        return (this.grid.getItems() as FrameBlockItem[]).flatMap(c => c.doorLeafRects());
    }

    // the stick a frame crosshair should default onto when unmounted: the stick the
    // item's placement rule names, else the latch (right) jamb, else any vertical jamb,
    // else any outline stick with area.
    private defaultFrameStick(item: HardwareItem): StickMaterial | undefined {
        const bySide = (side: string) => this.grid.outlineMats().find(p => p.side === side)?.mat;
        const ruled = ruleForItem(item)?.frameStick;
        const ruledSide = ruled === "hinge" ? hingeSideOrDefault(this.elev, true) : ruled;
        const preferred = ruledSide ? bySide(ruledSide) : undefined;
        if(preferred && this.stickHasArea(preferred)) return preferred;
        const right = bySide("right");
        if(right && this.stickHasArea(right)) return right;
        const mats = this.outlineStickMats().filter(m => this.stickHasArea(m));
        return mats.find(m => m.orientation === "vertical") ?? mats[0];
    }

    // the current target rect for a frame crosshair: its mounted stick's rect,
    // defaulting (and pinning) to a jamb when it has none. null until layout.
    private frameStickRect(p: MdHardwarePlacement, item: HardwareItem): CrosshairRect | null {
        let stick = p.stickId ? this.sticks.find(s => s.id === p.stickId) : undefined;
        const foundById = !!stick;
        if(!stick) {
            stick = this.defaultFrameStick(item);
            if(stick) p.stickId = stick.id;
        }
        const ok = !!stick && this.stickHasArea(stick);
        console.log("[hwx] frameStickRect", {
            view: this.elev.viewSettings.activeView.value,
            placement: p.id,
            stickId: p.stickId,
            foundById,
            defaulted: !foundById && !!stick,
            sticks: this.sticks.length,
            corners: this.corners.length,
            result: ok ? "OK" : (!stick ? "NULL(no-stick)" : "NULL(no-area)"),
        });
        if(!stick || !this.stickHasArea(stick)) return null;
        return this.stickRect(stick);
    }

    // the outline stick under `pt` (containment first, then nearest by centre), for
    // re-homing a dragged frame crosshair.
    private frameStickIdAt(pt: {x: number; y: number}): string | undefined {
        const mats = this.outlineStickMats().filter(m => this.stickHasArea(m));
        let best: StickMaterial | undefined;
        let bestDist = Infinity;
        for(const m of mats) {
            const r = this.stickRect(m);
            if(pt.x >= r.x0 && pt.x <= r.x1 && pt.y >= r.y0 && pt.y <= r.y1) return m.id;
            const cx = (r.x0 + r.x1) / 2, cy = (r.y0 + r.y1) / 2;
            const d = (pt.x - cx) ** 2 + (pt.y - cy) ** 2;
            if(d < bestDist) { bestDist = d; best = m; }
        }
        return best?.id;
    }

    // resolved width/height of every block and cell (by model id), read after a
    // layout. Used to seed sizes on blocks a structural edit creates.
    private collectSizes(): Map<string, {width: number; height: number}> {
        const out = new Map<string, {width: number; height: number}>();
        const walk = (b: TFrameBlock) => {
            if(b.width && b.height) out.set(b.model.id, {width: b.width.get(), height: b.height.get()});
            if(b instanceof FrameBlockRow || b instanceof FrameBlockColumn) {
                b.children.forEach(walk);
            }
        };
        walk(this.grid);
        return out;
    }

    render(jointDefault?: MdFrameJoint) {
        const spec = this.elev.frameSpec;
        // a step dropped by normalizeSteps, a stick moved off a void by
        // normalizeEdgeSticks, or a wrapper merged away by simplifyTree, changes the
        // tree, so the grid follows it. None runs during a drag.
        if(!this.elev.dragging) {
            normalizeSlopes(spec);
            normalizeAngles(spec);
            normalizeAirGaps(spec);
            const sticksBefore = sticksById(spec.frameBlocks);
            const stepped = normalizeSteps(spec);
            const edged = normalizeEdgeSticks(spec);
            const simplified = simplifyTree(spec);
            // hardware mounted on a stick a merge dropped moves to the stick that took
            // its place.
            for(const p of this.elev.hardware.list) {
                const to = p.stickId ? simplified.replacedSticks.get(p.stickId) : undefined;
                if(p.target === "frame" && to) p.stickId = to;
            }
            normalizeAnchors(spec, sticksBefore, simplified.replacedSticks);
            if(stepped || edged || simplified.changed) this.reconcileBlocks();
        }

        // the root block's rect is the frame's outer rect: the outline sticks are inside it.
        // A sloped stick's slot depends on its block's extent from the last layout, so
        // lay out twice when there is one (the first pass may have had no extent).
        try {
            let layout = recalcLayout(this.grid, 0, 0);
            if(Array.from(sticksById(spec.frameBlocks).values()).some(s => !!s.slope)) layout = recalcLayout(this.grid, 0, 0);
            this.elev.debugLayout.emit(layout);
        } catch (e: any) {
            console.warn(e);
            this.elev.draw.showError("Dimensions conflict");
        }

        // cells are placed: draw the interior sticks beside a void in pieces, then the
        // outline sticks along the frame outline.
        this.grid.layoutVoidParts();
        mergeJoinedSteps(spec, this.sticks);
        this.grid.renderOutline(spec);

        // an air gap is open space, so the joinery below only sees the stock sticks.
        const stock = stockSticks(this.sticks);
        meetAcrossSticks(stock);

        // positions/lengths are now known: find the outline's corners and cut every
        // stick end (miter by default; per-corner overrides win; every other end
        // square).
        const floorY = this.grid instanceof FrameBlockExt ? this.grid.floorY() : 0;
        const cells = (this.grid.getItems() as FrameBlockItem[])
            .filter(c => !!c.layoutRect)
            .map(c => ({open: c.model.kind === "void", rect: c.layoutRect!}));
        this.corners = detectCorners(stock, floorY, cells, this.grid.layoutRect);
        spec.cornerInfo = resolveTerminations(spec, stock, this.corners, jointDefault ?? spec.frameBlockJointDefault.value, floorY);

        // positions are final: detect interior cross/tee joints so the Selected
        // Stick ribbon can offer the cross flip / show tee state.
        spec.jointInfo = detectJoints(stock.filter(s => !s.destroyed && s.graphic.visible));

        // cache resolved sizes so a structural edit can seed sensible sizes on new
        // blocks (avoids the layout jumping after a cross flip).
        spec.sizeInfo = this.collectSizes();

        // re-attach each render: a grid rebuild swaps parentThing.lines (see outerDimension).
        // Drawn before the dimensions, which stand clear of the marks.
        this.parentThing.lines.addChild(this.anchorMarks.graphic);
        this.anchorMarks.draw();

        // overall outside-to-outside dimensions: layoutRects are now final. In
        // external-dimensions mode the inline outer +
        // per-cell dimensions hide themselves and the stacked set is drawn instead.
        this.outerDimension.draw(this.grid, spec);
        // the door's nominal sizes, stacked outside the frame inside the overall
        // dimensions (hides itself in external-dimensions mode).
        this.doorDimensions.draw(this.grid, spec);
        if(this.elev.viewSettings.externalDimensions.value) {
            this.exportDimensions.draw(this.grid, spec);
        }
        this.drawAngleDimensions();

        // frame crosshairs: re-attach to the current lines layer each render (a
        // grid rebuild swaps parentThing.lines for a fresh Graphics, orphaning
        // them — same reason FrameOuterDimension re-attaches), then redraw against
        // the now-final outer rect.
        console.log("[hwx] render", {
            view: this.elev.viewSettings.activeView.value,
            crosshairs: this.hardwareCrosshairs.length,
            sticks: this.sticks.length,
            corners: this.corners.length,
        });
        for(const t of this.hardwareCrosshairs) {
            this.parentThing.lines.addChild(t.graphic);
            t.draw();
        }

        this.elev.draw.currentScene?.applyLayout();
    }

    // label each angled mullion's plan angle above the frame, over the stick. Labels of
    // sticks that are no longer angled go.
    private drawAngleDimensions() {
        const rect = this.grid.layoutRect;
        const angled = new Map(Array.from(sticksById(this.elev.frameSpec.frameBlocks).values()).filter(s => !!s.angle).map(s => [s.id, s]));

        for(const [id, dim] of this.angleDimensions) {
            if(angled.has(id)) continue;
            dim.destroy();
            this.angleDimensions.delete(id);
        }

        for(const [id, stick] of angled) {
            const mat = this.sticks.find(s => s.id === id && !s.destroyed);
            if(!mat || !rect || !stick.angle) continue;

            let dim = this.angleDimensions.get(id);
            if(!dim) {
                dim = new StickAngleDimension(this.elev, deg => {
                    if(setStickAngle(this.elev.frameSpec, id, deg)) this.render();
                });
                this.angleDimensions.set(id, dim);
            }
            // re-attach each render: a grid rebuild swaps parentThing.lines (see outerDimension).
            this.parentThing.lines.addChild(dim.graphic);
            dim.draw(mat.positionX + mat.thickness.get() / 2, rect.y, stick.angle.get());
        }
    }

    createFrameBlock(param: FrameGridView, block: MdFrameBlock) {

        debugFrameBlocks(block);

        if(block.kind === "column") {
            return new FrameBlockColumn(param, block);
        } else if(block.kind === "row") {
            return new FrameBlockRow(param, block);
        }

        throw new Error("unknown block kind");
    }

    getLayout() {
        return FrmSceneLayout.group([
            this.parentThing
        ]);
    }

    destroy() {
        if(this.elev.frameGrid === this) this.elev.frameGrid = null;
        super.destroy();
        for(const t of this.hardwareCrosshairs) {
            if(!t.destroyed) t.destroy();
        }
        this.hardwareCrosshairs = [];
        this.outerDimension.destroy();
        this.exportDimensions.destroy();
        this.doorDimensions.destroy();
        this.anchorMarks.destroy();
        for(const dim of this.angleDimensions.values()) {
            if(!dim.destroyed) dim.destroy();
        }
        this.angleDimensions.clear();
        // parentThing is also a scene item (added via this.add() in the ctor), so
        // the scene's items-teardown may have already destroyed it — guard like the
        // crosshairs above to avoid a double-destroy warning.
        if(!this.parentThing.destroyed) this.parentThing.destroy();
    }
}


function collectModelItemIds(block: MdFrameBlock): string[] {
    const out: string[] = [];
    for(const c of block.children) {
        if(c.kind === "item") out.push(c.item.id);
        else if(c.kind === "stick") out.push(c.stick.id);
        else if(c.kind === "block") out.push(...collectModelItemIds(c.block));
    }
    return out;
}

function sameOrder(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((id, i) => id === b[i]);
}


export class FrameGridScene extends ManagedScene2 {
    setup(app: DrawFrmApp): void {
        super.setup(app);

        const grid = this.track(new FrameGridView(this.elev, input => this.add(input)));

        this.setupLayout(
            FrmSceneLayout.column(
                grid.getLayout().grow(1),
            )
                .grow(1)
                .gap(20)
                .padding(20),
        );
    }
}


function debugFrameBlocks(block: MdFrameBlock) {
    console.log(JSON.stringify(frameBlocksDebugTree(block), null, 2));
}